/**
 * How much of a dish there is — `FRONTEND.md` §9.63.
 *
 * The owner asked for gramáž on the customer menu and said where it lives: `products.unit_amount`
 * (*Množstvo*) and `products.unit` (*Jednotka*). So this reads two fields that already exist and
 * decides whether to draw them; nothing is stored and nothing is computed.
 *
 * **Measured on the restored menu** (2026-08-06): 173 products, 113 with an amount, 130 with a unit,
 * 104 with both — and **32 of the 113 already say it in their own name**. That last number is the
 * whole reason this file exists rather than a template string in a card.
 */

/** Only what this needs. Products arrive from the generated client with far more on them. */
export interface GramazSource {
  name?: string | null
  unitAmount?: string | number | null
  unit?: string | null
}

/** Units that may already be sitting on the end of an amount — `330ml`, `0,33l`, `50g`. */
const TRAILING_UNIT = /[a-zá-ž]+\s*$/i

function clean(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

/**
 * The amount and unit as one string, or null when there is nothing to say.
 *
 * The two are joined only when the unit is present **and** the amount does not already end in one:
 * `Pepsi Max 0,33l` is stored as `unit_amount = "330ml"` with an empty `unit`, and other rows carry
 * the unit separately. That is a rule about the data as it is rather than as it ought to be.
 */
export function gramazText(product: GramazSource): string | null {
  const amount = clean(product.unitAmount)
  if (amount === '') return null
  const unit = clean(product.unit)
  if (unit === '' || TRAILING_UNIT.test(amount)) return amount
  return `${amount} ${unit}`
}

/**
 * Whether the product's own name already states this amount.
 *
 * Compared with spaces and the decimal separator folded, because a name writes it the way a person
 * would — *Pepsi Max 0,33l* against `0.33` + `l`, *Cheddar dresing 50g* against `50` + `g`.
 *
 * Deliberately not a substring test on the amount alone: `50` appears in *Kofola 0,5l* and in half
 * the drinks list, and suppressing on that would hide the gramáž of products that never showed it.
 */
export function nameAlreadySaysIt(product: GramazSource): boolean {
  const text = gramazText(product)
  if (text === null) return false
  const name = normalise(product.name)
  return name.includes(normalise(text)) || name.includes(normalise(text.replace(/\s+/g, '')))
}

function normalise(value: string | null | undefined): string {
  return clean(value).toLocaleLowerCase('sk').replace(/,/g, '.').replace(/\s+/g, ' ')
}

/**
 * What the menu should draw, or null.
 *
 * Null for the 60 products with no amount, and for the 32 whose name already carries it — printing
 * *Cheddar dresing 50g · 50 g* on nearly a third of the menu reads as a defect rather than as
 * information, and would be reported back as one.
 */
export function gramazFor(product: GramazSource): string | null {
  return nameAlreadySaysIt(product) ? null : gramazText(product)
}
